import type {
  CompRef,
  TpaStyle,
  TpaStyleParamValue,
} from '@wix/document-services-types';
import { AbstractExternalAPIHandler } from '../AbstractExternalAPIHandler';
import { OOIStyleEncoder } from './OOIStyleEncoder';
import { CacheMethodResponse } from '../../api/decorators/CacheMethodResponse';
import { MethodCacheStrategy } from '../../api/MethodCacheStrategy';
import { ManifestComponentExtensionType } from '../../stores';
import { DependencyToken } from '../../dependencies';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export class ExternalOOIStylesHandler extends AbstractExternalAPIHandler {
  /**
   * optimization for OOI + global colors
   * as soon as color is changed, it applies changes for
   */
  private readonly setStylesQueue: Map<
    string,
    {
      editorPointer: EditorPointer<CompRef>;
      styles: {
        cssProperties?: Record<string, string>;
        cssCustomProperties?: Record<string, string>;
      };
    }[]
  > = new Map();

  private readonly scheduledMicrotask: Map<string, unknown> = new Map();

  /**
   * cache reason – OOI styles getter
   * works with compRef, so it ignores innerElementPath
   * means that we can share any OOI styles call for ROOT and its children
   */
  @CacheMethodResponse<[EditorPointer<CompRef>]>({
    namespace: 'ExternalOOIStylesHandler',
    cacheStrategy: MethodCacheStrategy.StylesUpdate,
    buildCacheKey: (_self, editorPointer) => {
      const documentPointer =
        _self.editorPointerAPI!.getDocumentPointer(editorPointer);
      return _self.editorPointerAPI!.getEditorPointerUniqueId(
        documentPointer,
        undefined,
        { ignoreVariants: false },
      );
    },
    extractEditorPointers: (_self, editorPointer) => [
      _self.editorPointerAPI!.buildEditorPointer(
        _self.editorPointerAPI!.getDocumentPointer(editorPointer),
      ),
    ],
  })
  private getOOIStyleValues(editorPointer: EditorPointer<CompRef>) {
    const getStyle = this.dependenciesContributor.get(
      DependencyToken.OOI_STYLE_GET_EFFECTIVE,
    );
    return getStyle(editorPointer) as TpaStyle;
  }

  getStyles(
    editorPointer: EditorPointer<CompRef>,
    options?: {
      customGetDefaultStyle: () => {
        cssProperties: Record<string, string>;
        cssCustomProperties: Record<string, string>;
      };
    },
  ): {
    cssProperties?: Record<string, string>;
    cssCustomProperties?: Record<string, string>;
  } {
    /**
     * TODO: maybe we should not use store directly here
     * mb we need to pass definitions from the arguments
     */
    const manifest = this.manifestStore.getResolvedEditorElement(
      editorPointer,
      {
        useRawManifest: true,
      },
    );

    const metadata =
      this.manifestStore.getMetadataByEditorPointer<ManifestComponentExtensionType.OOI>(
        editorPointer,
      );

    const cssCustomProperties = manifest?.cssCustomProperties;

    if (!cssCustomProperties || !Object.keys(cssCustomProperties).length) {
      return {
        cssCustomProperties: {},
        cssProperties: {},
      };
    }

    const ooiStyleValues = this.getOOIStyleValues(editorPointer);

    const values: Record<string, string> = {};

    if (!metadata) {
      return {
        cssCustomProperties: {},
        cssProperties: {},
      };
    }

    const encoder = new OOIStyleEncoder(
      manifest,
      ooiStyleValues,
      metadata.statics,
      options?.customGetDefaultStyle?.() ?? {
        cssCustomProperties: {},
        cssProperties: {},
      },
    );

    Object.entries(cssCustomProperties).forEach(([key]) => {
      const _value = encoder.decode(key);

      if (typeof _value !== 'undefined' && _value !== null) {
        values[key] = _value;
      }
    });

    return {
      cssProperties: {},
      cssCustomProperties: values,
    };
  }

  setStyles(
    editorPointer: EditorPointer<CompRef>,
    styles: {
      cssProperties?: Record<string, string>;
      cssCustomProperties?: Record<string, string>;
    },
  ) {
    const ooiEditorPointer = this.editorPointerAPI.buildEditorPointer(
      this.editorPointerAPI.getDocumentPointer(editorPointer),
    );

    const idx = getOOIQueueIdx(ooiEditorPointer);

    if (!this.setStylesQueue.has(idx)) {
      this.setStylesQueue.set(idx, []);
    }

    this.setStylesQueue.set(idx, [
      ...this.setStylesQueue.get(idx)!,
      {
        editorPointer,
        styles,
      },
    ]);

    if (this.scheduledMicrotask.has(idx)) {
      return;
    }

    /**
     * the queue is used for OOI to group multiple calls for OOI's root and its children
     * not sure if we need it 100%, at the moment there are not a lot of such cases,
     * but from the logic perspective it makes sense
     * note - we cannot use timeout, since change might be happening within the single event loop inside a DM transaction,
     * so we should be sure setter is called before transaction's end
     *
     * flow is enabled for browser only
     */
    if (
      typeof window !== 'undefined' &&
      typeof globalThis?.queueMicrotask === 'function'
    ) {
      this.scheduledMicrotask.set(idx, true);

      globalThis.queueMicrotask(() => {
        this.flushSetStylesQueue(ooiEditorPointer);
        this.setStylesQueue.delete(idx);
        this.scheduledMicrotask.delete(idx);
      });
    } else {
      this.flushSetStylesQueue(ooiEditorPointer);
      this.setStylesQueue.delete(idx);
    }
  }

  private flushSetStylesQueue(ooiEditorPointer: EditorPointer<CompRef>) {
    const queue = this.setStylesQueue.get(getOOIQueueIdx(ooiEditorPointer));

    if (!queue?.length) {
      return;
    }

    const values: TpaStyleParamValue[] = [];

    queue.forEach(({ editorPointer, styles }) => {
      const manifest =
        this.manifestStore.getResolvedEditorElement(editorPointer);

      const metadata =
        this.manifestStore.getMetadataByEditorPointer<ManifestComponentExtensionType.OOI>(
          editorPointer,
        );

      if (!manifest || !metadata) {
        return;
      }

      const getStyle = this.dependenciesContributor.get(
        DependencyToken.OOI_STYLE_GET_EFFECTIVE,
      );
      const ooiStyleValues = getStyle(editorPointer) as TpaStyle;

      if (!metadata) {
        return;
      }

      const encoder = new OOIStyleEncoder(
        manifest,
        ooiStyleValues,
        metadata.statics,
        {
          cssCustomProperties: {},
          cssProperties: {},
        },
      );

      values.push(
        ...Object.entries(styles.cssCustomProperties ?? {})
          .map(([key, value]) => {
            return encoder.encode(key, value);
          })
          .filter((x) => !!x),
      );
    });

    if (values.length) {
      const setStyle = this.dependenciesContributor.get(
        DependencyToken.OOI_STYLE_UPDATE,
      );
      setStyle(ooiEditorPointer, values);
    }
  }
}

function getOOIQueueIdx(editorPointer: EditorPointer<CompRef>) {
  /**
   * For OOI we do not care about the inner elements path
   */
  return JSON.stringify(editorPointer?.documentPointer);
}
